/**
 * notificationMatrix — derive the Email/SMS/Push preference matrix from the
 * shape the API actually returns (#2652).
 *
 * The load-bearing design decision: the matrix is built FROM THE ADVERTISED
 * PREF KEYS in the `preferences` object the backend sends, NOT from a hardcoded
 * list. Every `${channel}_${bucket}` boolean flag becomes a cell; `*_enabled`
 * flags are per-channel master toggles. So:
 *
 *  - The response carries email_* + sms_* per-type keys, the sms_enabled /
 *    push_enabled masters, AND the per-type push keys (push_event_reminders,
 *    push_rsvp_confirmations, push_event_updates, push_club_admin_activity,
 *    push_chat_messages from #2617/0059, plus push_club_updates from #1445/0061).
 *    Each becomes a cell automatically — the Push column for the Club
 *    Announcements row (push_club_updates) and the push-only chat_messages row
 *    both derive from the advertised keys, no per-cell frontend wiring.
 *  - A response that omits some per-type push keys (an older/cached envelope, or
 *    a channel exposing only its master) still renders: the absent cells simply
 *    show as unavailable. The derivation degrades gracefully in either direction.
 *
 * This module is pure (no React, no imports from higher layers) so it is unit
 * tested directly against both response shapes.
 */

export const NOTIFICATION_CHANNELS = ['email', 'sms', 'push'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export interface BucketMeta {
  /** Human label for the notification type (row). */
  label: string;
  /** One-line description shown under the row label. */
  description: string;
}

/**
 * Canonical display order + copy for the notification-type buckets we know
 * about. Order here IS the row order. A bucket that appears in the API response
 * but is missing from this map still renders (appended, alphabetically) with a
 * title-cased fallback label — so a future pref key is never silently dropped.
 */
export const NOTIFICATION_BUCKET_META: Record<string, BucketMeta> = {
  invites: { label: 'Club Invitations', description: "When you're invited to join a club" },
  event_reminders: { label: 'Event Reminders', description: "Upcoming events you've RSVP'd to" },
  rsvp_confirmations: { label: 'RSVP Confirmations', description: 'Confirmation when you RSVP to an event' },
  event_updates: { label: 'Event Updates', description: 'When event details change' },
  club_updates: { label: 'Club Announcements', description: 'Important updates from your club leaders' },
  club_admin_activity: { label: 'Club Activity', description: 'New members joining clubs you manage' },
  chat_messages: { label: 'Chat Messages', description: 'New messages in your club chats' },
  digest: { label: 'Weekly Digest', description: 'Summary of club activity and upcoming events' },
};

const BUCKET_ORDER = Object.keys(NOTIFICATION_BUCKET_META);

/** Title-case a bucket id (`some_new_bucket` -> `Some New Bucket`). */
function titleCase(bucket: string): string {
  return bucket
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function bucketMeta(bucket: string): BucketMeta {
  return (
    NOTIFICATION_BUCKET_META[bucket] ?? {
      label: titleCase(bucket),
      description: '',
    }
  );
}

/** Build the pref key for a (channel, bucket) cell — pure string join. */
export function cellKey(channel: NotificationChannel, bucket: string): string {
  return `${channel}_${bucket}`;
}

/** Build the master pref key for a channel (`sms` -> `sms_enabled`). */
export function masterKeyFor(channel: NotificationChannel): string {
  return `${channel}_enabled`;
}

export interface NotificationMatrix {
  /** Channels that expose at least one flag, in email -> sms -> push order. */
  channels: NotificationChannel[];
  /** Channels that expose a `*_enabled` master toggle, mapped to that key. */
  masterKey: Partial<Record<NotificationChannel, string>>;
  /** Notification-type buckets present as a per-type key on any channel, ordered. */
  buckets: string[];
  /** Every boolean pref key the response advertised (masters + per-type cells). */
  presentKeys: Set<string>;
}

/**
 * Derive the matrix from a `preferences` payload. Only boolean-valued
 * `${email|sms|push}_*` keys are considered; `user_id` / `updated_at` and any
 * other non-boolean metadata are ignored by construction.
 */
export function buildNotificationMatrix(
  preferences: Record<string, unknown>,
): NotificationMatrix {
  const presentKeys = new Set<string>();
  const channelsPresent = new Set<NotificationChannel>();
  const masterKey: Partial<Record<NotificationChannel, string>> = {};
  const bucketsPresent = new Set<string>();

  for (const [key, value] of Object.entries(preferences)) {
    if (typeof value !== 'boolean') continue;
    const match = /^(email|sms|push)_(.+)$/.exec(key);
    if (!match) continue;
    const channel = match[1] as NotificationChannel;
    const bucket = match[2];

    presentKeys.add(key);
    channelsPresent.add(channel);

    if (bucket === 'enabled') {
      masterKey[channel] = key;
    } else {
      bucketsPresent.add(bucket);
    }
  }

  const channels = NOTIFICATION_CHANNELS.filter((c) => channelsPresent.has(c));
  const known = BUCKET_ORDER.filter((b) => bucketsPresent.has(b));
  const unknown = [...bucketsPresent].filter((b) => !BUCKET_ORDER.includes(b)).sort();

  return {
    channels,
    masterKey,
    buckets: [...known, ...unknown],
    presentKeys,
  };
}
